/**
 * A settings row, and the controls that go in one.
 *
 * Three columns — **name · value · verb** — with the hint *under* the line, in
 * the same 16px gutter the name is inset by. The value column has a `minWidth`
 * and not a `width`, so a select that needs more takes it and the column of
 * verbs still lines up — and the verb column is exactly one verb wide, so the
 * lock sits against the control it locks rather than across a gap.
 *
 * There is **no dot** in that gutter on an organization's own pages. It was
 * teal on every row the organization had answered, and the idea was that a
 * column of them could be read down at a glance. What it read as in practice
 * was a page half in alarm: answering a setting is the ordinary thing to do
 * there — it is what the page is for — and marking the ordinary act leaves the
 * *unanswered* rows looking like the ones in order. What such a row has
 * answered is said by the verb it carries, which is the difference the reader
 * can act on: **Reset** exists only where there is something to give back.
 *
 * A **workspace's** rows kept a teal dot on everything they had answered, on
 * the argument that there the proportion is the other way round — three
 * overrides out of twenty-seven, so the marked row is the exception. It went the
 * same way as the organization's, and for a better reason than symmetry: a
 * workspace's controls are live from the start too now, and a row that has not
 * been answered says so in the value column, in words, with the name of the
 * layer it is borrowing from. A dot saying the same thing one column to the
 * left is a second alphabet for one fact.
 *
 * What `mark` is left for is the one state neither the control nor the tag can
 * show, and the one this whole lot was built for: an override the organization
 * has since locked away, written down, kept, and applying to nothing.
 *
 * And the value and the lock sit on **one** row, because the parser refuses a
 * setting "enforced with no value to enforce": a rule that is obvious when the
 * two are side by side, and invisible when the values are a textarea and the
 * locks are a grid of checkboxes on another card.
 */
import type React from 'react';
import { color, font, radius } from '../theme/tokens';
import { ChevronDown, LockMark } from './icons';
import { useTooltip } from './Tooltip';

/** The 16px gutter every hint and every wide editor is inset by. */
const GUTTER = 16;

/**
 * What the gutter says about a row, where a row says anything at all.
 *
 * `dead` is an override the organization has locked away over the top of.
 * Absent is every other row, answered here or not — which the value column
 * already tells apart.
 */
export type SettingMark = 'dead';

export function SettingRow({
  name,
  settingKey,
  hint,
  /** What the row shows in its value column. A wide row has none. */
  value,
  /** Reset, where there is something to reset, and the lock. */
  action,

  /** A full-width editor under the line: the only row that is a document. */
  wide,
  /** Why this row may not apply — a dependency the document cannot express. */
  depends,
  /** The state dot in the gutter. Workspace rows only — see the note above. */
  mark,
  /**
   * Why what this row holds is not what a session gets.
   *
   * It sits under the line, on the same gutter as the hint, and it is drawn
   * *instead* of the hint when both would show: a row that has just been told
   * its value is dormant does not also need the sentence explaining what the
   * setting would have done.
   */
  warn,
  was,
  source,
  keyLabel,
  children
}: {
  name: string;
  settingKey: string;
  /** A sentence, or — where a row's answer needs saying in several lines — those lines. */
  hint?: React.ReactNode;
  value?: React.ReactNode;
  action?: React.ReactNode;
  wide?: boolean;
  depends?: string;
  mark?: SettingMark;
  warn?: React.ReactNode;
  /**
   * What the published version says, where the draft has moved this row (§11.2
   * « changé »): an amber dot before the label and « Was … » under the hint.
   */
  was?: string;
  /**
   * Where the value comes from, on a workspace's row (§11.2, §46.7): « Inherited
   * from », « Overrides » or « Locked by » and the layer's tag, under the hint.
   */
  source?: React.ReactNode;
  /**
   * The key is the label (§44.5, a catalogue row): `name` is drawn in mono
   * 13/500 and the key is not written a second time beside it.
   */
  keyLabel?: boolean;
  children?: React.ReactNode;
}): React.JSX.Element {
  const dot = mark !== undefined || was !== undefined;
  return (
    // The redesign's setting row (§11.1): 14 above and below, a #E0E0E5
    // hairline, the label 13.5/500 with its key in mono 11.5 #6E6E73, the
    // control in a 320 column and the lock 8 after it.
    // On a phone (globals.css, .al-setting) the row stacks: label, help, then
    // the control full width with its lock.
    <div
      role="group"
      aria-label={name}
      className="al-setting"
      style={{ display: 'flex', flexDirection: 'column', padding: '14px 0', boxShadow: `inset 0 -1px 0 ${color.line}` }}
    >
      <div className="al-setting-line" style={{ display: 'flex', alignItems: 'center', gap: GUTTER, minHeight: 32 }}>
        <div
          className="al-setting-label"
          style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 10, paddingLeft: dot ? 0 : GUTTER }}
        >
          {dot && (
            <span
              aria-hidden="true"
              style={{
                flex: 'none',
                width: 6,
                height: 6,
                marginLeft: 5,
                marginRight: 5,
                borderRadius: radius.chip,
                background: was !== undefined && !mark ? color.warnText : color.yellow,
                boxShadow: was !== undefined && !mark ? 'none' : '0 0 0 3px rgba(242,201,76,.2)'
              }}
            />
          )}
          <span style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 9 }}>
            <span
              style={{ fontFamily: keyLabel ? font.mono : undefined, fontSize: keyLabel ? 13 : 13.5, fontWeight: 500, color: color.ink }}
            >
              {name}
            </span>
            {!keyLabel && (
              <span
                style={{
                  fontFamily: font.mono,
                  fontSize: 11.5,
                  color: color.inkFaint,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {settingKey}
              </span>
            )}
          </span>
        </div>
        {/* A wide row has no value column at all: the editor below IS the
            value, and putting a document in a 288px column is the reason it
            used to end up as JSON. */}
        {!wide && (
          <div className="al-setting-value" style={{ flex: 'none', minWidth: 320, display: 'flex', justifyContent: 'flex-end' }}>
            {value}
          </div>
        )}
        {/* One verb wide, and no wider. It was sized for two — *Reset* beside
            the lock — and kept that width after *Reset* left, which put 50px
            of nothing between every control and the padlock that holds it
            shut. They belong on the same line and next to each other: the
            parser refuses a setting enforced with no value to enforce, and
            side by side that is self-evident. */}
        <div
          className="al-setting-action"
          style={{ flex: 'none', width: 64, marginLeft: -8, display: 'flex', justifyContent: 'flex-start', gap: 6 }}
        >
          {action}
        </div>
      </div>
      {warn ? (
        <div
          style={{
            maxWidth: 560,
            paddingLeft: GUTTER,
            marginTop: 6,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            fontSize: 12,
            lineHeight: 1.45,
            color: color.warnText
          }}
        >
          <span aria-hidden="true" style={{ flex: 'none', marginTop: 1, display: 'flex' }}>
            <LockMark size={12} />
          </span>
          <span style={{ flex: 1, minWidth: 0 }}>{warn}</span>
        </div>
      ) : (
        hint && (
          <div style={{ maxWidth: 560, paddingLeft: GUTTER, marginTop: 4, fontSize: 12, lineHeight: 1.45, color: color.inkFaint }}>
            {hint}
          </div>
        )
      )}
      {source && (
        <div
          style={{
            maxWidth: 560,
            paddingLeft: GUTTER,
            marginTop: 6,
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 8,
            fontSize: 12.5,
            color: color.inkSubtle
          }}
        >
          {source}
        </div>
      )}
      {was !== undefined && (
        <div style={{ maxWidth: 560, paddingLeft: GUTTER, marginTop: 4, fontSize: 12.5, lineHeight: 1.45, color: color.warnText }}>
          Was {was}
        </div>
      )}
      {/* « ↳ » and the product's sentence, neutral (§11.2): a dependency is not
          an alert, so it reads in the quiet ink and not in amber. */}
      {depends && (
        <div style={{ maxWidth: 560, paddingLeft: GUTTER, marginTop: 4, display: 'flex', gap: 6, fontSize: 13, lineHeight: 1.5, color: color.inkMuted }}>
          <span aria-hidden="true" style={{ flex: 'none' }}>
            ↳
          </span>
          <span>{depends}</span>
        </div>
      )}
      {children && (
        <div className="al-setting-more" style={{ paddingLeft: GUTTER, marginTop: 10 }}>
          {children}
        </div>
      )}
    </div>
  );
}

/**
 * Which layer a row is still borrowing its value from.
 *
 * The quiet half of `InheritedValue`, on its own, because a workspace's control
 * is live before anybody has answered it: the tag is then the only thing on the
 * row saying that what it shows is the organization's answer and not this
 * workspace's. Grey and not a colour — it marks where a value comes from, which
 * is not a state anybody has to act on.
 */
export function SourceTag({ children, title }: { children: React.ReactNode; title?: string }): React.JSX.Element {
  return (
    <span
      title={title}
      style={{
        flex: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        height: 17,
        padding: '0 5px',
        border: `1px solid ${color.line}`,
        borderRadius: 4,
        background: color.surfaceAlt,
        fontFamily: font.mono,
        fontSize: 9.5,
        letterSpacing: '.06em',
        color: color.inkGhost
      }}
    >
      {children}
    </span>
  );
}

/** What the layer above says, and which layer that is — for a reader who cannot write. */
export function InheritedValue({
  from,
  title,
  children
}: {
  from: string;
  /** What that layer actually is, for a tag whose three letters cannot say it. */
  title?: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <span style={{ display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0, maxWidth: 320 }}>
      <SourceTag title={title}>{from}</SourceTag>
      <span
        style={{
          minWidth: 0,
          fontFamily: font.mono,
          fontSize: 12.5,
          color: color.inkSubtle,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
      >
        {children}
      </span>
    </span>
  );
}

/** A locked setting loses its control: there is nothing left to decide. */
export function LockedValue({ by, children }: { by: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <span style={{ display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0, maxWidth: 320 }}>
      <span
        style={{
          flex: 'none',
          display: 'inline-flex',
          alignItems: 'center',
          height: 17,
          padding: '0 5px',
          border: `1px solid ${color.accentRule}`,
          borderRadius: 4,
          background: color.accentWash,
          fontFamily: font.mono,
          fontSize: 9.5,
          letterSpacing: '.06em',
          color: color.accentText,
          textTransform: 'uppercase'
        }}
      >
        {by}
      </span>
      <span
        style={{
          minWidth: 0,
          fontFamily: font.mono,
          fontSize: 12.5,
          color: color.ink,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
      >
        {children}
      </span>
    </span>
  );
}

/** A row's verb: legible at rest, and never a fill. */
export function Verb({
  children,
  onClick,
  title,
  disabled
}: {
  children: React.ReactNode;
  onClick: () => void;
  title?: string;
  disabled?: boolean;
}): React.JSX.Element {
  return (
    <button
      type="button"
      className="verb"
      onClick={onClick}
      title={title}
      disabled={disabled}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 24,
        padding: '0 9px',
        border: `1px solid ${color.line}`,
        borderRadius: radius.sm,
        background: 'transparent',
        fontFamily: 'inherit',
        fontSize: 11,
        color: color.inkSubtle,
        opacity: disabled ? 0.45 : 0.85,
        whiteSpace: 'nowrap',
        cursor: disabled ? 'default' : 'pointer'
      }}
    >
      {children}
    </button>
  );
}

/**
 * The padlock — the only fill a row carries, and it is ink, because what it
 * does is answer for everybody in the organization at once.
 */
export function LockToggle({
  on,
  onToggle,
  disabled
}: {
  on: boolean;
  onToggle: () => void;
  disabled?: boolean;
}): React.JSX.Element {
  return (
    <button
      type="button"
      className="verb"
      aria-pressed={on}
      onClick={onToggle}
      disabled={disabled}
      title={
        disabled
          ? 'Give it a value first — a setting cannot be enforced with nothing to enforce.'
          : on
            ? 'Locked: a member cannot change this on their machine.'
            : 'Lock it: every member gets this value and cannot change it.'
      }
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        height: 24,
        padding: '0 8px',
        border: `1px solid ${on ? color.ink : color.line}`,
        borderRadius: radius.sm,
        background: on ? color.ink : 'transparent',
        color: on ? '#FFFFFF' : color.inkSubtle,
        fontFamily: 'inherit',
        fontSize: 11,
        opacity: disabled ? 0.45 : 1,
        cursor: disabled ? 'default' : 'pointer'
      }}
    >
      <LockMark size={11} />
      Lock
    </button>
  );
}

/* ── the controls, one per kind the validator keeps ─────────────────────── */

/**
 * The border and the fill of a control holding a value that is not applying.
 *
 * Amber and **not** the refusal red: nothing here is wrong. The value is a
 * legitimate preference, kept because the organization may unlock the setting
 * tomorrow, and the row says out loud that it is dormant rather than throwing
 * it away — which is what refusing the input would do.
 */
const DORMANT: React.CSSProperties = { borderColor: color.yellow, background: color.warnBg, color: color.warnText };

export function SettingSelect({
  value,
  options,
  onChange,
  wide,
  dead
}: {
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  wide?: boolean;
  /** Held, but overruled by a lock above — see `DORMANT`. */
  dead?: boolean;
}): React.JSX.Element {
  return (
    <span style={{ position: 'relative', display: 'flex', width: wide ? '100%' : 320 }}>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        style={{
          appearance: 'none',
          width: '100%',
          height: 32,
          padding: '0 30px 0 11px',
          border: `1px solid ${color.lineInput}`,
          borderRadius: radius.md,
          background: color.surface,
          fontFamily: 'inherit',
          fontSize: 13,
          color: color.ink,
          cursor: 'pointer',
          ...(dead ? DORMANT : {})
        }}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <span
        aria-hidden="true"
        style={{ position: 'absolute', right: 11, top: 0, bottom: 0, display: 'flex', alignItems: 'center', color: color.inkFaint }}
      >
        <ChevronDown />
      </span>
    </span>
  );
}

/**
 * A number with its own bounds written beside it.
 *
 * The bounds are shown rather than enforced by clamping, because the server
 * refuses a value it would have had to change — so a number outside them has
 * to be visible here, not silently rounded into something nobody typed.
 */
export function SettingNumber({
  value,
  min,
  max,
  step,
  unit,
  onChange,
  dead
}: {
  value: number;
  min: number;
  max: number;
  step?: number;
  unit?: string;
  onChange: (value: number) => void;
  /** Held, but overruled by a lock above — see `DORMANT`. */
  dead?: boolean;
}): React.JSX.Element {
  const outside = value < min || value > max;
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 10, width: 320, justifyContent: 'flex-end' }}>
      <span style={{ fontFamily: font.mono, fontSize: 10.5, color: outside ? color.badText : color.inkGhost, textAlign: 'right' }}>
        {unit ? `${unit} · ` : ''}
        {min}–{max}
      </span>
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(event) => onChange(Number(event.target.value))}
        style={{
          width: 112,
          height: 34,
          padding: '0 11px',
          border: `1px solid ${outside ? color.badText : color.lineInput}`,
          borderRadius: radius.md,
          background: color.surface,
          fontFamily: font.mono,
          fontSize: 13,
          color: color.ink,
          textAlign: 'right',
          ...(dead ? DORMANT : {})
        }}
      />
    </span>
  );
}

export function SettingText({
  value,
  placeholder,
  onChange,
  dead,
  invalid,
  held
}: {
  value: string;
  placeholder?: string;
  onChange: (value: string) => void;
  /** Held, but overruled by a lock above — see `DORMANT`. */
  dead?: boolean;
  /** What is wrong with the value, said under the field in red (item 89). */
  invalid?: string;
  /** Why the field cannot be typed in yet (rule 6): held at 40 %, focusable, the reason in its tooltip. */
  held?: string;
}): React.JSX.Element {
  const { trigger, tip } = useTooltip<HTMLInputElement>(held, 'start');
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 6, maxWidth: '100%' }}>
      <input
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        readOnly={Boolean(held)}
        aria-disabled={held ? true : undefined}
        aria-invalid={invalid ? true : undefined}
        {...trigger}
        style={{
          width: 320,
          maxWidth: '100%',
          height: 34,
          padding: '0 11px',
          border: `1px solid ${invalid ? color.red : color.lineInput}`,
          borderRadius: radius.md,
          background: color.surface,
          fontFamily: font.mono,
          fontSize: 13,
          color: color.ink,
          ...(held ? { opacity: 0.4, cursor: 'default' } : {}),
          ...(dead ? DORMANT : {})
        }}
      />
      {tip}
      {invalid && (
        <span role="alert" style={{ maxWidth: 320, fontSize: 12.5, lineHeight: 1.45, color: color.badText }}>
          {invalid}
        </span>
      )}
    </span>
  );
}

/** A switch. On is a fill, and a fill is ink. */
export function SettingToggle({ on, onToggle }: { on: boolean; onToggle: () => void }): React.JSX.Element {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={onToggle}
      style={{
        position: 'relative',
        flex: 'none',
        width: 32,
        height: 19,
        padding: 0,
        border: `1px solid ${on ? color.ink : color.lineInput}`,
        borderRadius: radius.chip,
        background: on ? color.ink : 'transparent',
        cursor: 'pointer'
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 2,
          left: on ? 15 : 2,
          width: 13,
          height: 13,
          borderRadius: radius.chip,
          background: on ? '#FFFFFF' : color.inkGhost,
          transition: 'left .12s'
        }}
      />
    </button>
  );
}

/**
 * A set of values rather than one — which agents a member may launch.
 *
 * A segmented control and not checkboxes, because the answer is never empty:
 * `enabledAgents` falls back to Claude Code whatever is stored, so the last
 * one on cannot be turned off and the control says so by refusing the click.
 */
export function SettingSet({
  value,
  options,
  onChange,
  allowEmpty
}: {
  value: string[];
  options: { value: string; label: string }[];
  onChange: (value: string[]) => void;
  /** Whether nothing chosen is an answer — a list where empty means "any" (an agent's models). */
  allowEmpty?: boolean;
}): React.JSX.Element {
  return (
    <span
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        padding: 2,
        border: `1px solid ${color.line}`,
        borderRadius: radius.md,
        background: color.surfaceAlt
      }}
    >
      {options.map((option) => {
        const on = value.includes(option.value);
        const last = on && value.length === 1 && !allowEmpty;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={on}
            disabled={last}
            title={last ? 'The last agent cannot be turned off — an organization that offers none offers no product.' : undefined}
            onClick={() =>
              onChange(on ? value.filter((one) => one !== option.value) : [...value, option.value])
            }
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 26,
              padding: '0 12px',
              border: 0,
              borderRadius: radius.sm,
              background: on ? color.surface : 'transparent',
              boxShadow: on ? '0 1px 2px rgba(22,25,31,.06)' : undefined,
              fontFamily: 'inherit',
              fontSize: 12.5,
              color: on ? color.ink : color.inkSubtle,
              cursor: last ? 'default' : 'pointer'
            }}
          >
            {option.label}
          </button>
        );
      })}
    </span>
  );
}
